import { z } from 'zod';
import { DisplayName, MemberStatus, Role, Username } from './members';

// What the member admin API (A4; D23) takes and answers, for the API and the Members screens (A5).
// Its routes are under /api/admin/members, for admins only. Every request that changes something is
// JSON, `{}` when it says nothing more (D21), and every refusal is `{ error: <code> }` (API_ERRORS,
// auth.ts): `forbidden` to a member who isn't an admin, `not_found` for an unknown member,
// `username_taken`, `own_account` and `last_admin` for the rules below, `invalid` for the rest.

/** A member as an admin sees them (D15): never the password's hash. */
export const AdminMember = z.object({
  id: z.string(),
  username: z.string(),
  displayName: z.string(),
  role: Role,
  status: MemberStatus,
  /** True until the member replaces the one-time password an admin gave them (D5, D20). */
  mustChangePassword: z.boolean(),
  /** When the account was made: an ISO time in UTC. */
  createdAt: z.string(),
});
export type AdminMember = z.infer<typeof AdminMember>;

/** `GET /api/admin/members`: every member, by username. */
export const MembersResponse = z.object({ members: z.array(AdminMember) });
export type MembersResponse = z.infer<typeof MembersResponse>;

/**
 * `POST /api/admin/members`: a new member, a plain member unless the role says otherwise. The
 * server makes their one-time password (D23); a username another member has is `username_taken`.
 */
export const CreateMemberRequest = z.object({
  username: Username,
  displayName: DisplayName,
  role: Role.default('member'),
});
export type CreateMemberRequest = z.infer<typeof CreateMemberRequest>;

/**
 * `PATCH /api/admin/members/:id`: any of the three, at least one. A changed username is the one the
 * member logs in with from then on; their sessions go on. An admin may change their own name and
 * username, never their own role (`own_account`), and never the last active admin's (`last_admin`).
 */
export const EditMemberRequest = z
  .object({
    username: Username.optional(),
    displayName: DisplayName.optional(),
    role: Role.optional(),
  })
  .refine((edit) => Object.values(edit).some((value) => value !== undefined), 'nothing to change');
export type EditMemberRequest = z.infer<typeof EditMemberRequest>;

/**
 * The body of disable, enable, the password's reset and remove, which have nothing to say: `{}`,
 * sent as JSON all the same, so a form on another site can't send it (D21).
 */
export const EmptyRequest = z.object({});
export type EmptyRequest = z.infer<typeof EmptyRequest>;

/** What the edit, disable and enable answer: the member as they are now. */
export const MemberResponse = z.object({ member: AdminMember });
export type MemberResponse = z.infer<typeof MemberResponse>;

/**
 * What creating a member (201) and resetting a password answer: the member, and the one-time
 * password the server made, given this once and nowhere else (D23). The member logs in with it and
 * chooses their own at once (D5, D20).
 */
export const OneTimePasswordResponse = z.object({ member: AdminMember, password: z.string() });
export type OneTimePasswordResponse = z.infer<typeof OneTimePasswordResponse>;

/** What removing a member answers, as logout does. */
export const OkResponse = z.object({ ok: z.literal(true) });
export type OkResponse = z.infer<typeof OkResponse>;
